import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const UserActionMetrics = ({ dateRange, refreshTrigger }) => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState({
    agentToggles: [],
    subscriptionChanges: [],
    errorRates: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('toggles');

  useEffect(() => {
    loadMetrics();

    // Set up real-time subscription
    const channel = supabase?.channel('user_action_metrics')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'agent_usage_metrics'
        },
        () => {
          loadMetrics();
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [dateRange, refreshTrigger, user]);

  const getDateFilter = () => {
    const now = new Date();
    let startDate;

    switch (dateRange) {
      case '24h':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '7d':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90d':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      default:
        return null;
    }

    return startDate?.toISOString();
  };

  const loadMetrics = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      let startDate = getDateFilter();

      // Build query with date filter
      let query = supabase?.from('agent_usage_metrics')?.select('*')?.order('created_at', { ascending: true });

      if (startDate) {
        query = query?.gte('created_at', startDate);
      }

      const { data: usageData, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Error loading metrics:', error?.message);
          setMetrics({ agentToggles: [], subscriptionChanges: [], errorRates: [] });
          return;
        }
      }

      if (usageData && usageData?.length > 0) {
        processMetricsData(usageData);
      } else {
        setMetrics({ agentToggles: [], subscriptionChanges: [], errorRates: [] });
      }
    } catch (error) {
      console.error('Error loading user action metrics:', error);
      setMetrics({ agentToggles: [], subscriptionChanges: [], errorRates: [] });
    } finally {
      setLoading(false);
    }
  };

  const isSchemaError = (error) => {
    if (!error) return false;
    if (error?.code && typeof error?.code === 'string') {
      const errorClass = error?.code?.substring(0, 2);
      if (errorClass === '42' || errorClass === '08') return true;
    }
    return false;
  };

  const processMetricsData = (usageData) => {
    // Group by date for agent toggles
    const togglesMap = {};
    const subscriptionMap = {};
    const errorMap = {};

    usageData?.forEach(metric => {
      const date = new Date(metric?.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const metadataObj = metric?.metadata || {};

      // Agent toggles (from metadata)
      if (metadataObj?.action === 'enable' || metadataObj?.action === 'disable') {
        if (!togglesMap?.[date]) {
          togglesMap[date] = { date, enables: 0, disables: 0 };
        }
        if (metadataObj?.action === 'enable') {
          togglesMap[date].enables++;
        } else {
          togglesMap[date].disables++;
        }
      }

      // Subscription changes (from metadata)
      if (metadataObj?.subscription_change) {
        if (!subscriptionMap?.[date]) {
          subscriptionMap[date] = { date, upgrades: 0, downgrades: 0, cancellations: 0 };
        }
        const changeType = metadataObj?.subscription_change;
        if (changeType === 'upgrade') subscriptionMap[date].upgrades++;
        if (changeType === 'downgrade') subscriptionMap[date].downgrades++;
        if (changeType === 'cancel') subscriptionMap[date].cancellations++;
      }

      // Error rates (from metadata)
      if (metadataObj?.error || metadataObj?.recovery) {
        if (!errorMap?.[date]) {
          errorMap[date] = { date, errors: 0, recoveries: 0, total: 0 };
        }
        if (metadataObj?.error) errorMap[date].errors++;
        if (metadataObj?.recovery) errorMap[date].recoveries++;
        errorMap[date].total++;
      }
    });

    // Calculate error rates
    const errorRates = Object.values(errorMap)?.map(item => ({
      ...item,
      rate: item?.total > 0 ? ((item?.errors / item?.total) * 100)?.toFixed(1) : 0
    }));

    setMetrics({
      agentToggles: Object.values(togglesMap),
      subscriptionChanges: Object.values(subscriptionMap),
      errorRates
    });
  };

  const tabs = [
    { id: 'toggles', label: 'Agent Toggles', icon: 'ToggleLeft' },
    { id: 'subscriptions', label: 'Subscription Changes', icon: 'CreditCard' },
    { id: 'errors', label: 'Error Rates', icon: 'AlertCircle' }
  ];

  const getTotalToggles = () => {
    return metrics?.agentToggles?.reduce((sum, item) => sum + item?.enables + item?.disables, 0);
  };

  const getTotalSubscriptionChanges = () => {
    return metrics?.subscriptionChanges?.reduce((sum, item) => sum + item?.upgrades + item?.downgrades + item?.cancellations, 0);
  };

  const getAverageErrorRate = () => {
    if (metrics?.errorRates?.length === 0) return '0.0';
    const avg = metrics?.errorRates?.reduce((sum, item) => sum + parseFloat(item?.rate || 0), 0) / (metrics?.errorRates?.length || 1);
    return avg?.toFixed(1);
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border shadow-warm p-6">
        <p className="text-muted-foreground text-center">Loading metrics...</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border shadow-warm p-6 space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="ToggleLeft" size={20} color="var(--color-primary)" />
            <span className="text-sm text-muted-foreground font-caption">Total Toggles</span>
          </div>
          <p className="text-2xl font-semibold text-foreground font-heading">{getTotalToggles()}</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="CreditCard" size={20} color="var(--color-blue-500)" />
            <span className="text-sm text-muted-foreground font-caption">Subscription Changes</span>
          </div>
          <p className="text-2xl font-semibold text-foreground font-heading">{getTotalSubscriptionChanges()}</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="AlertCircle" size={20} color="var(--color-orange-500)" />
            <span className="text-sm text-muted-foreground font-caption">Avg Error Rate</span>
          </div>
          <p className="text-2xl font-semibold text-foreground font-heading">{getAverageErrorRate()}%</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`
              flex items-center gap-2 px-4 py-2 font-caption font-medium transition-colors
              ${activeTab === tab?.id 
                ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }
            `}
          >
            <Icon name={tab?.icon} size={18} />
            {tab?.label}
          </button>
        ))}
      </div>

      {/* Charts */}
      <div className="h-80">
        {metrics?.agentToggles?.length === 0 && metrics?.subscriptionChanges?.length === 0 && metrics?.errorRates?.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">No data available for the selected date range</p>
          </div>
        ) : (
          <>
            {activeTab === 'toggles' && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={metrics?.agentToggles}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="date" stroke="var(--color-muted-foreground)" />
                  <YAxis stroke="var(--color-muted-foreground)" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--color-card)', 
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px'
                    }} 
                  />
                  <Legend />
                  <Bar dataKey="enables" fill="var(--color-green-500)" name="Enables" />
                  <Bar dataKey="disables" fill="var(--color-red-500)" name="Disables" />
                </BarChart>
              </ResponsiveContainer>
            )}

            {activeTab === 'subscriptions' && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={metrics?.subscriptionChanges}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="date" stroke="var(--color-muted-foreground)" />
                  <YAxis stroke="var(--color-muted-foreground)" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--color-card)', 
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px'
                    }} 
                  />
                  <Legend />
                  <Bar dataKey="upgrades" fill="var(--color-green-500)" name="Upgrades" />
                  <Bar dataKey="downgrades" fill="var(--color-orange-500)" name="Downgrades" />
                  <Bar dataKey="cancellations" fill="var(--color-red-500)" name="Cancellations" />
                </BarChart>
              </ResponsiveContainer>
            )}

            {activeTab === 'errors' && (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={metrics?.errorRates}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="date" stroke="var(--color-muted-foreground)" />
                  <YAxis stroke="var(--color-muted-foreground)" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--color-card)', 
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px'
                    }} 
                  />
                  <Legend />
                  <Line type="monotone" dataKey="errors" stroke="var(--color-red-500)" name="Errors" strokeWidth={2} />
                  <Line type="monotone" dataKey="recoveries" stroke="var(--color-green-500)" name="Recoveries" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UserActionMetrics;